import React, { useEffect, useState } from 'react';
import { useHCompanies } from '../hooks/HCompanies';
import { LoadingSpinner } from '../../utils/LoadingSpinner';
import GCompaniesTable from '../../tables/GCompaniesTable';
import CompanyForm from '../../dialogs/CompanyForm';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { API_BASE } from '../../../config/constants';

export default function CompaniesTab() {
    const { companies, loading: companiesLoading, error: companiesError, loadCompanies, registerUpdateCompany } = useHCompanies();
    const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState<any | null>(null);
    const [companyFormRenderKey, setCompanyFormRenderKey] = useState(0);
    const [companySearchInput, setCompanySearchInput] = useState('');

    useEffect(() => { void loadCompanies(); }, [loadCompanies]);

    const displayedCompanies = companies.filter((company) => {
        const query = companySearchInput.trim().toLowerCase();
        if (!query) return true;

        const name = String(company.company_name ?? company.company ?? company.name ?? '').toLowerCase();
        const email = String(company.email ?? '').toLowerCase();
        const country = String(company.country ?? '').toLowerCase();

        return name.includes(query) || email.includes(query) || country.includes(query);
    });

    const handleAddCompany = async (data: any) => {
        try {
            await registerUpdateCompany({
                id: data.id ?? editingCompany?.id ?? editingCompany?.company_id ?? null,
                name: data.name,
                email: data.email,
                phoneNumber: data.phoneNumber,
                country: data.country,
                role: data.role,
                role_id: null,
            });
            setCompanyDialogOpen(false);
            setEditingCompany(null);
        } catch (err) {
            console.error('Failed to add company', err);
        }
    };

    const handleDeleteCompany = async (company: any) => {
        try {
            const companyId = company.id ?? company.company_id;
            const response = await fetch(`${API_BASE}/api/users/delete-company-by-id`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ company_id: companyId }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result?.success) throw new Error(result?.message || 'Failed to delete company.');
            await loadCompanies();
        } catch (err) {
            console.error('Failed to delete company', err);
        }
    };

    const openAddCompanyDialog = () => {
        setEditingCompany(null);
        setCompanyDialogOpen(true);
        setCompanyFormRenderKey((p) => p + 1);
    };

    const openEditCompanyDialog = (company: any) => {
        setEditingCompany(company);
        setCompanyDialogOpen(true);
        setCompanyFormRenderKey((p) => p + 1);
    };

    return (
        <div className='standard-div'>
            <div className='standard-div-flex'>
                <h1>Companies</h1>
                <div className='header-flex'>
                    <TextField
                        size='small'
                        placeholder='Search companies by name, email or country...'
                        value={companySearchInput}
                        onChange={(event) => setCompanySearchInput(event.target.value)}
                        sx={{ marginRight: 1, minWidth: 300 }}
                    />
                    <Button variant='contained' color='primary' onClick={openAddCompanyDialog}>
                        Add Company
                    </Button>
                </div>

                <CompanyForm
                    key={`company-form-${companyFormRenderKey}`}
                    open={companyDialogOpen}
                    onClose={() => { setCompanyDialogOpen(false); setEditingCompany(null); }}
                    onSubmit={handleAddCompany}
                    title={editingCompany ? 'Edit Company' : 'Add Company'}
                    submitText={editingCompany ? 'Update Company' : 'Add Company'}
                    initialValues={editingCompany ? {
                        id: editingCompany.id ?? editingCompany.company_id,
                        name: String(editingCompany.company_name ?? editingCompany.company ?? editingCompany.name ?? ''),
                        email: String(editingCompany.email ?? ''),
                        phoneNumber: String(editingCompany.phone_number ?? editingCompany.phone ?? ''),
                        country: String(editingCompany.country ?? ''),
                        role: String(editingCompany.role_name ?? editingCompany.role ?? ''),
                    } : undefined}
                />

                {companiesLoading ? (
                    <LoadingSpinner text='Loading companies...' />
                ) : companiesError ? (
                    <LoadingSpinner text={companiesError} />
                ) : displayedCompanies.length === 0 ? (
                    <LoadingSpinner text='No companies found' />
                ) : (
                    <GCompaniesTable
                        companies={displayedCompanies}
                        onEditCompany={openEditCompanyDialog}
                        handleDelete={handleDeleteCompany}
                    />
                )}
            </div>
        </div>
    );
}
